import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Button, Card, Filter, FilterTypes, FilterValues, PageHeader, QuickFilterTypes, Table } from "@cofano/cck";
import { Box } from "@mui/material";

import { Language, usePermission } from "imports/Utils";

import { TableId, useBackendTable } from "components/table";
import Helmet from "components/helmet/Helmet";

import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";

import { RelationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import CharterTypeSelect from "./components/selects/CharterTypeSelect";

import { ChildPropsFromPagination } from "types/commons";
import { CharterType, RelationType, SortDirection } from "__generated__/enums";
import { GetChartersDocument, GetChartersQuery } from "__generated__/view/charters/graphql/charter.queries.generated";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";

type CharterFromQuery = ChildPropsFromPagination<GetChartersQuery, "charters">;

const Charters = () => {

	const client = useApolloClient();
	const navigate = useNavigate();

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.Charters,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetChartersDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "createdOn",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							charterNumber: filter.charterNumber,
							charterType: filter.charterType,
							creditors: filter.creditor?.map((option: RelationSelectOption) => option.id),
							debtors: filter.debtor?.map((option: RelationSelectOption) => option.id),
							brokers: filter.broker?.map((option: RelationSelectOption) => option.id),
							includeParentContractCharters: true,
						},
					},
				});

				callback({
					content: result.data.charters.content ?? [],
					totalResults: result.data.charters.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { isReadOnly, isRTR } = usePermission();

	const openCharterSideviewer = useOpenCharterSideviewer();

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("CHARTER_ORDERS")}
					<Helmet title={intl.formatMessage({ id: "CHARTER_ORDERS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && isRTR && (
						<Button onClick={() => navigate("/view/salescharters/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
							}, {
								id: "charterType",
								title: intl.formatMessage({ id: "TYPE" }),
								renderFilterValue: (value) => intl.formatMessage({ id: value }),
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "broker",
								title: intl.formatMessage({ id: "BROKER" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowClick={row => {
						if (row.id) {
							openCharterSideviewer(row.id, () => refetch());
						}
					}}
					onRowDoubleClick={row => {
						const charter = row as CharterFromQuery;
						navigate(charter.type === CharterType.Sales ? `/view/salescharters/${charter.id}/details` : `/view/purchasecharters/${charter.id}/details`);
					}}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "charterType",
								title: intl.formatMessage({ id: "TYPE" }),
								render: (value, onChange) => <CharterTypeSelect value={value} onChange={onChange} />,
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple
									/>
								),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple
									/>
								),
							}, {
								id: "broker",
								title: intl.formatMessage({ id: "BROKER" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										types={[ RelationType.Broker ]}
										multiple
									/>
								),
							} ]} />
					)}
					columns={[ {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
						width: 150,
					}, {
						id: "charterType",
						title: intl.formatMessage({ id: "TYPE" }),
						width: 150,
						render: (row) => <CharterTypeTag type={row.type} />,
						filterComponent: (value, onChange) => (
							<CharterTypeSelect
								label={intl.formatMessage({ id: "TYPE" })}
								value={value}
								onChange={onChange}
							/>
						),
					}, {
						id: "creditor",
						title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
						sortable: true,
						sortKey: "creditor.name",
						width: 200,
						render: row => <RelationLink relation={row.creditor} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple
							/>
						),
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
						sortable: true,
						sortKey: "debtor.name",
						width: 200,
						render: row => <RelationLink relation={row.debtor} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple
							/>
						),
					}, {
						id: "broker",
						title: intl.formatMessage({ id: "BROKER" }),
						width: 200,
						render: row => <RelationLink relation={row.broker} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "BROKER" })}
								value={value}
								onChange={options => onChange(options)}
								types={[ RelationType.Broker ]}
								multiple
							/>
						),
					}, {
						id: "createdOn",
						title: intl.formatMessage({ id: "CREATED" }),
						sortable: true,
						width: 150,
						render: row => <FormattedDate date={row.createdOn} withoutYear />,
					} ]} />
			</Card>
		</div>
	);
};

export default Charters;
